import { useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";

const socialLinks = [
  {
    name: "LinkedIn",
    description: "Connect with me professionally",
    href: "https://www.linkedin.com/in/kacper-agatowski-342607274/",
  },
  {
    name: "GitHub",
    description: "View my repositories and code",
    href: "https://github.com/KacperA02",
  },
  {
    name: "Discord",
    description: "Message me directly",
    href: "https://discordapp.com/users/kapper2967",
  },
];

const initialFormData = {
  name: "",
  email: "",
  message: "",
};

const Contact = ({ embedded = false }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState({
    type: "",
    message: "",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (status.message) {
      setStatus({
        type: "",
        message: "",
      });
    }
  };

  const handleSend = async (event) => {
    event.preventDefault();

    if (isSending) {
      return;
    }

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      console.error("Missing EmailJS environment variables", {
        serviceIdExists: Boolean(serviceId),
        templateIdExists: Boolean(templateId),
        publicKeyExists: Boolean(publicKey),
      });

      setStatus({
        type: "error",
        message: "The contact form is not configured correctly.",
      });

      return;
    }

    setIsSending(true);
    setStatus({
      type: "",
      message: "",
    });

    try {
      const result = await emailjs.sendForm(
        serviceId,
        templateId,
        event.currentTarget,
        {
          publicKey,
        }
      );

      console.log("Email sent:", result.status, result.text);

      setFormData(initialFormData);

      setStatus({
        type: "success",
        message: "Your message has been sent. I’ll get back to you soon.",
      });
    } catch (error) {
      console.error("EmailJS error:", error);

      setStatus({
        type: "error",
        message:
          error?.text ||
          "Your message could not be sent. Please try again later.",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section
      id="contact"
      className={`scroll-mt-20 border-t border-zinc-200 bg-white px-6 sm:px-10 lg:px-16 ${
        embedded ? "py-24 lg:py-32" : "min-h-screen py-20"
      }`}
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-indigo-600">
            Contact
          </p>

          <h2 className="mt-4 text-4xl font-semibold tracking-tight text-zinc-950 sm:text-5xl">
            Let’s talk about software, projects, or opportunities
          </h2>

          <p className="mt-6 text-lg leading-8 text-zinc-600">
            Send me a message through the form or reach out through one of my
            profiles. I’m open to graduate software engineering roles and
            relevant development opportunities.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-8 lg:grid-cols-[0.8fr_1.2fr]">
          {/* Social links */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl border border-zinc-200 bg-zinc-50 p-6 sm:p-8"
          >
            <h3 className="text-2xl font-semibold text-zinc-950">
              Find me online
            </h3>

            <p className="mt-3 leading-7 text-zinc-600">
              LinkedIn is the best place for professional messages. You can also
              view my work on GitHub.
            </p>

            <div className="mt-8 space-y-4">
              {socialLinks.map((link) => (
                <motion.a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ x: 4 }}
                  className="group flex items-center justify-between rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm transition hover:border-zinc-300 hover:shadow-md"
                >
                  <div>
                    <p className="font-semibold text-zinc-950">
                      {link.name}
                    </p>

                    <p className="mt-1 text-sm text-zinc-500">
                      {link.description}
                    </p>
                  </div>

                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-100 text-zinc-700 transition group-hover:bg-zinc-950 group-hover:text-white">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M7 17L17 7M17 7H9M17 7V15"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </motion.a>
              ))}
            </div>

            <div className="mt-8 rounded-2xl border border-zinc-200 bg-white p-5">
              <p className="text-sm font-medium text-zinc-500">
                Based in
              </p>

              <p className="mt-1 font-semibold text-zinc-950">
                Dublin, Ireland
              </p>
            </div>
          </motion.div>

          {/* Contact form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl border border-zinc-200 bg-white p-6 shadow-xl shadow-zinc-200/50 sm:p-8"
          >
            <h3 className="text-2xl font-semibold text-zinc-950">
              Send a message
            </h3>

            <p className="mt-3 text-zinc-600">
              Fill in the form and your message will be sent directly to me.
            </p>

            <form onSubmit={handleSend} className="mt-8 space-y-6">
              <div>
                <label
                  htmlFor="contact-name"
                  className="mb-2 block text-sm font-medium text-zinc-700"
                >
                  Name
                </label>

                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  placeholder="Your name"
                  required
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-950 outline-none transition placeholder:text-zinc-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                />
              </div>

              <div>
                <label
                  htmlFor="contact-email"
                  className="mb-2 block text-sm font-medium text-zinc-700"
                >
                  Email
                </label>

                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  required
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-950 outline-none transition placeholder:text-zinc-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                />
              </div>

              <div>
                <label
                  htmlFor="contact-message"
                  className="mb-2 block text-sm font-medium text-zinc-700"
                >
                  Message
                </label>

                <textarea
                  id="contact-message"
                  name="message"
                  placeholder="Tell me what you would like to discuss"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full resize-none rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-950 outline-none transition placeholder:text-zinc-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                />
              </div>

              {status.message && (
                <div
                  role="status"
                  className={`rounded-xl border px-4 py-3 text-sm ${
                    status.type === "success"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-red-200 bg-red-50 text-red-800"
                  }`}
                >
                  {status.message}
                </div>
              )}

              <button
                type="submit"
                disabled={isSending}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-zinc-950 px-6 py-3.5 font-semibold text-white shadow-lg shadow-zinc-950/10 transition hover:-translate-y-0.5 hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {isSending ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Sending
                  </>
                ) : (
                  <>
                    Send message

                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M22 2L11 13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M22 2L15 22L11 13L2 9L22 2Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;